import React, { useEffect, useMemo, useRef, useState } from "react";
import MosaicErrorBoundary from "./errorboundary";
import "./index.css";
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface Pillar        { num: string; title: string; body: string; }
interface Initiative    { idx: string; name: string; full: string; desc: string; badge: string; }
interface InfraCondition{ cat: string; metric: string; label: string; signals: string[]; }
interface AtlData       { n: string; label: string; }
interface ThesisItem    { num: string; title: string; body: string; }
interface PartnerForm   { name: string; email: string; org: string; type: string; message: string; }
type FormErrors = Partial<Record<keyof PartnerForm, string>>;

/* ─── Assets ─────────────────────────────────────────────────────────────── */
const LOGO_SRC = "/logo192.png";

/* ─── Static Data ────────────────────────────────────────────────────────── */
const PILLARS: Pillar[] = [
  { num: "01", title: "Coordination Layer",    body: "The biggest barriers are usually fragmented coordination, misaligned incentives, and weak deployment systems. Mosaic exists to close those gaps." },
  { num: "02", title: "Systems Thinking",      body: "Every intervention should compound. We identify where systems are breaking down and design ventures, partnerships, and infrastructure that address the root issue." },
  { num: "03", title: "Purposeful Capitalism", body: "We build ventures that are commercially durable and socially useful. Profitability and positive impact are not competing goals." },
  { num: "04", title: "Equitable Access",      body: "Communities and organizations should participate in major technological transitions, not be excluded from them. Lowering barriers is part of the design." },
  { num: "05", title: "Regional Leverage",     body: "Regional infrastructure and innovation ecosystems compound into broader impact. Atlanta is the first operating wedge, not the limit." },
];

const INITIATIVES: Initiative[] = [
  {
    idx: "01", name: "ADII",
    full: "Atlanta Distributed Infrastructure Initiative",
    desc: "Edge compute coordination grounded in public and market data. ADII is an execution-oriented pilot program focused on site readiness, utility coordination, and deployment planning.",
    badge: "In Development",
  },
];

const INFRA_CONDITIONS: InfraCondition[] = [
  {
    cat: "Power Demand",
    metric: "9,885 MW",
    label: "Georgia PSC authorized 9,885 MW of new generation capacity in December 2025, with approximately 90% earmarked for data centers.",
    signals: [
      "Final PSC order: Dockets 56298 / 56310 (Dec 19, 2025)",
      "Framework enables over 10 GW of future data center expansion capacity (CBRE, H2 2025)",
      "Base rates frozen through 2028 to protect residential customers",
    ],
  },
  {
    cat: "Market Position",
    metric: "#2",
    label: "Metro Atlanta ranked as the second-largest U.S. data center market by total installed capacity at year-end 2025, behind Northern Virginia.",
    signals: [
      "1,459 MW total inventory, up 458.8 MW year-over-year (CBRE, Q4 2025)",
      "456 MW of net absorption recorded in 2025 despite record delivery volume (CBRE, Mar 2026)",
      "Vacancy tightened to 2% despite record delivery volume",
    ],
  },
  {
    cat: "Enterprise Modernization",
    metric: "High",
    label: "A significant share of enterprise workloads continues to run on legacy infrastructure, sustaining elevated modernization demand across most sectors.",
    signals: [
      "AI-ready infrastructure remains early-stage across many regions",
      "Edge deployment continues to be a high-growth, nascent category",
    ],
  },
  {
    cat: "Investment Pipeline",
    metric: "$40B+",
    label: "Georgia attracted approximately $40 billion in new data center commitments in 2025, ranking second nationally behind Virginia.",
    signals: [
      "Campus proposals now reach multi-billion-dollar scale",
      "Investment volume expected to increase substantially in 2026 as large-scale development completes (CBRE, H2 2025)",
      "State tax structure and business climate continue to attract operators",
    ],
  },
  {
    cat: "Interconnection Pressure",
    metric: "Multi-year",
    label: "Grid interconnection study timelines now stretch multiple years across congested U.S. queues, creating a coordination bottleneck well before projects become a capital problem.",
    signals: [
      "Over 2,060 GW of capacity actively seeking grid connection nationally (LBNL, 2025)",
      "Transmission constraints emerging in Georgia as multiple mega-scale projects attempt to come online simultaneously (CBRE, H2 2025)",
      "Utility coordination is routinely the primary constraint for new deployments",
    ],
  },
  {
    cat: "Site Readiness",
    metric: "Limited",
    label: "Only a small fraction of the active development pipeline is pre-certified for rapid deployment, leaving significant runway for site-readiness coordination work.",
    signals: [
      "Site-to-deployment timelines continue to stretch 28–36 months on average",
      "Dark fiber reuse opportunities remain under-coordinated across the region",
    ],
  },
];

const ATL_DATA: AtlData[] = [
  { n: "#2",       label: "U.S. data center market by capacity — metro Atlanta (CBRE, Q4 2025)" },
  { n: "$40B+",    label: "Georgia data center commitments in 2025" },
  { n: "9,885 MW", label: "New generation capacity authorized by Georgia PSC, Dec 2025" },
  { n: "12 mo",    label: "ADII operating targets for the first cycle" },
];

const THESIS: ThesisItem[] = [
  { num: "01", title: "The Problem Is Coordination, Not Technology",  body: "Many large problems already have adequate technology. What is missing is coordination: aligned incentives, functional deployment systems, and institutions that can bridge the gap." },
  { num: "02", title: "Execution Before Abstraction",                 body: "Mosaic validates its ideas through pilots, partners, and data. Short execution cycles with measurable targets compound faster than strategy decks." },
  { num: "03", title: "Infrastructure Is the Foundation",             body: "Physical and digital infrastructure underlies every economy. Treating it as an afterthought creates fragility; treating it as a core asset creates leverage." },
  { num: "04", title: "Regional Ecosystems Create Leverage",          body: "National transformation is built locally. Strengthening one region's coordination capacity and infrastructure base can create compounding returns far beyond that geography." },
  { num: "05", title: "Profit and Impact Are the Same Thesis",        body: "Commercially unscalable missions fade. Extractive growth eventually collapses. Durable ventures are built where financial strength and measurable benefit reinforce each other." },
];

/* ─── Deterministic tile helpers ─────────────────────────────────────────── */
function seededValue(i: number, salt: number, min: number, max: number): number {
  const raw = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return min + (raw - Math.floor(raw)) * (max - min);
}

function buildHeroTiles(count: number) {
  return Array.from({ length: count }, (_, i) => {
    const primary = [12, 23, 34, 45, 56, 67, 78, 89].includes(i % 100);
    const lo  = primary ? seededValue(i, 1, 0.18, 0.40) : i % 7 === 0 ? seededValue(i, 2, 0.05, 0.12) : seededValue(i, 3, 0.01, 0.04);
    const hi  = primary ? seededValue(i, 1, 0.32, 0.52) : i % 7 === 0 ? seededValue(i, 2, 0.10, 0.20) : seededValue(i, 3, 0.03, 0.07);
    const dur = primary ? seededValue(i, 7, 2.5, 5)      : i % 7 === 0 ? seededValue(i, 9, 3, 6)       : seededValue(i, 11, 4, 8);
    const del = primary ? seededValue(i, 8, 0, 2)        : i % 7 === 0 ? seededValue(i, 10, 0, 3)      : seededValue(i, 12, 0, 4);
    return { lo: lo.toFixed(2), hi: hi.toFixed(2), dur, del };
  });
}

function buildAtlantaTiles(): string[] {
  return Array.from({ length: 100 }, (_, i) => {
    const primary = [12, 23, 34, 45, 56, 67, 78, 89].includes(i % 100);
    const alpha   = primary ? seededValue(i, 4, 0.20, 0.42) : i % 7 === 0 ? seededValue(i, 5, 0.06, 0.14) : seededValue(i, 6, 0.01, 0.04);
    return `rgba(255,255,255,${alpha.toFixed(2)})`;
  });
}

function smoothScrollTo(id: string) {
  if (typeof window === "undefined") return;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function MosaicVenturesComponent(): React.ReactElement {
  const [scrolled,     setScrolled]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [formSent,     setFormSent]     = useState(false);
  const [formLoading,  setFormLoading]  = useState(false);
  const [formError,    setFormError]    = useState("");
  const [errors,       setErrors]       = useState<FormErrors>({});
  const [form,         setForm]         = useState<PartnerForm>({ name: "", email: "", org: "", type: "", message: "" });
  const [honeypot,     setHoneypot]     = useState("");
  const formOpenedAt   = useRef<number>(0);
  const observerRef    = useRef<IntersectionObserver | null>(null);

  /* Scroll listener */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Intersection observer for reveal animations */
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.08 }
    );
    const t = window.setTimeout(() => {
      document.querySelectorAll<HTMLElement>(".reveal").forEach((el) => observerRef.current?.observe(el));
    }, 100);
    return () => { clearTimeout(t); observerRef.current?.disconnect(); };
  }, []);

  /* Escape key — closes both modal and mobile menu */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (modalOpen)    closeModal();
        if (mobileOpen)   setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, mobileOpen]);

  /* Lock body scroll when modal or mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = (modalOpen || mobileOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modalOpen, mobileOpen]);

  /* Record time when modal opens for timing bot check */
  useEffect(() => {
    if (modalOpen) formOpenedAt.current = Date.now();
  }, [modalOpen]);

  const tiles    = useMemo(() => buildHeroTiles(140), []);
  const atlTiles = useMemo(() => buildAtlantaTiles(), []);

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.name.trim())  e.name  = "Full name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof PartnerForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { if (!prev[field]) return prev; const n = { ...prev }; delete n[field]; return n; });
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    // BOT PROTECTION 1 — Honeypot
    if (honeypot) {
      console.warn("[MosaicVentures] Honeypot field filled out. Aborting submission silently.");
      setFormSent(true);
      return;
    }

    // BOT PROTECTION 2 — Timing check
    const elapsed = Date.now() - formOpenedAt.current;
    if (elapsed < 3000) {
      console.warn(`[MosaicVentures] Form submitted too quickly (${(elapsed / 1000).toFixed(1)}s). Blocked to prevent spam.`);
      setFormError("Please review your information and try again.");
      return;
    }

    if (!validate()) return;

    // Environment key safeguard
    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
    if (!accessKey) {
      console.error("[MosaicVentures] REACT_APP_WEB3FORMS_ACCESS_KEY is not set. Form submissions will fail.");
      setFormError("Form is not configured. Please email contact@mosaicventures.co directly.");
      return;
    }

    setFormLoading(true);
    try {
      // ❌ REMOVED: console.log("Attempting submission...")
      
      const res  = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: accessKey,
          subject:    `New Partnership Inquiry from ${form.name}`,
          from_name:  "Mosaic Ventures Website",
          ...form,
        }),
      });
      
      const json = await res.json();
      // ❌ REMOVED: console.log("Web3Forms Raw Response Data:", json)

      if (!json.success) throw new Error(json.message || `Status ${res.status}`);
      
      setFormSent(true);
      setForm({ name: "", email: "", org: "", type: "", message: "" });
      setErrors({});
      setHoneypot("");
    } catch (error) {
      // ✅ KEPT: Crucial error tracking for unexpected live API downtime or network loss
      console.error("[MosaicVentures] Critical form submission error:", error);
      setFormError("Something went wrong. Please email contact@mosaicventures.co directly.");
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormSent(false);
    setFormError("");
    setErrors({});
    setHoneypot("");
  };

  return (
    <div className="mv">
      {/* ── Dynamic SEO & Meta Isolation Layer ── */}
      <Helmet>
        <title>Mosaic Ventures | Infrastructure & Venture Coordination in Atlanta</title>
        <meta name="title" content="Mosaic Ventures | Infrastructure & Venture Coordination in Atlanta" />
        <meta name="description" content="Mosaic Ventures builds mission-aligned ventures, coordinates infrastructure systems, and develops regional ecosystems. Starting in Atlanta, scaling globally." />
        <meta name="keywords" content="Venture Builder, Infrastructure Coordination, AI Infrastructure, Data Centers, Economic Development, Atlanta Ventures, Tech Modernization" />
        <link rel="canonical" href="https://mosaicventures.co/" />

        {/* Open Graph Content Previews */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mosaicventures.co/" />
        <meta property="og:title" content="Mosaic Ventures | Coordination Layer for Impact-Driven Systems" />
        <meta property="og:description" content="We build mission-aligned ventures and coordinate infrastructure systems. Atlanta is our first operating wedge, not the limit." />
        <meta property="og:image" content="https://mosaicventures.co/logo192.png" />
        <meta property="og:site_name" content="Mosaic Ventures" />

        {/* Twitter Card Configurations */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://x.com/mosaic_ventures/" />
        <meta property="twitter:title" content="Mosaic Ventures | Infrastructure & Venture Coordination" />
        <meta property="twitter:description" content="Building systems that scale with purpose. Mission-aligned venture building and infrastructure coordination." />
        <meta property="twitter:image" content="https://pbs.twimg.com/profile_images/1719543525593731072/GajF2NeK_400x400.jpg" />
        <meta property="twitter:site" content="@mosaic_ventures" />

        {/* Homepage Specific Structured Data Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Mosaic Ventures – Coordination Layer for Impact-Driven Systems",
              "description": "Mosaic Ventures builds mission-aligned ventures, coordinates infrastructure systems, and develops regional ecosystems that create long-term societal impact while remaining commercially scalable.",
              "url": "https://mosaicventures.co",
              "inLanguage": "en-US"
            })
          }}
        />
      </Helmet>

      <a className="skip-link" href="#main">Skip to content</a>

      {/* ── Navigation ── */}
      <nav className={`mv-nav${scrolled ? " scrolled" : ""}`} aria-label="Main navigation">
        <a className="nav-brand" href="#hero" aria-label="Mosaic Ventures home">
          <img src={LOGO_SRC} alt="" width={30} height={30} />
          Mosaic Ventures
        </a>

        <ul className="nav-links">
          <li><a href="#mission">Mission</a></li>
          <li><a href="#initiatives">Initiatives</a></li>
          <li><a href="#conditions">Intelligence</a></li>
          <li><a href="#atlanta">Why Atlanta</a></li>
          <li><a href="#thesis">Thesis</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>

        <button type="button" className="nav-cta" onClick={() => setModalOpen(true)}>
          Work With Us
        </button>

        <button
          type="button" className="menu-button"
          aria-expanded={mobileOpen} aria-label="Toggle mobile menu"
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? "Close" : "Menu"}
        </button>
      </nav>

      {/* ── Mobile menu ── */}
      <div className={`mobile-menu${mobileOpen ? " open" : ""}`} role="dialog" aria-modal="true" aria-label="Mobile navigation">
        <ul>
          {[["#mission","Mission"],["#initiatives","Initiatives"],["#conditions","Intelligence"],["#atlanta","Why Atlanta"],["#thesis","Thesis"],["#contact","Contact"]].map(([href, label]) => (
            <li key={href}><a href={href} onClick={() => setMobileOpen(false)}>{label}</a></li>
          ))}
        </ul>
      </div>

      {/* ── Hero ── */}
      <header>
        <section className="mv-hero" id="hero" aria-labelledby="hero-heading">
          <div className="tile-canvas" aria-hidden="true">
            {tiles.map((t, i) => (
              <div key={i} className="tile-cell"
                style={{ "--lo": t.lo, "--hi": t.hi, "--dur": `${t.dur}s`, "--delay": `${t.del}s` } as React.CSSProperties}
              />
            ))}
          </div>

          <div className="hero-inner">
            <p className="hero-eyebrow">Coordination Layer · Venture Builder · Atlanta, GA</p>
            <h1 className="hero-h1" id="hero-heading">
              Building<br />
              systems that<br />
              <em>scale with purpose</em>
            </h1>
            <p className="hero-p">
              Mosaic Ventures builds mission-aligned ventures, coordinates infrastructure systems,
              and develops regional ecosystems that create long-term societal impact while remaining
              commercially scalable. The current wedge is AI infrastructure, but the operating model is broader.
            </p>
            <div className="hero-actions">
              <button type="button" className="btn-gold" onClick={() => smoothScrollTo("initiatives")}>Explore Initiatives</button>
              <button type="button" className="btn-ghost" onClick={() => smoothScrollTo("thesis")}>Read Our Thesis</button>
            </div>
            <div className="hero-stats" aria-label="Company context">
              {([
                { n: "2023",  l: "Founded in Atlanta" },
                { n: "ATL",   l: "Initial Operating Wedge" },
                { n: "ADII",  l: "Active Pilot Program" },
              ]).map((s) => (
                <div className="hstat" key={s.n}>
                  <div className="hstat-n">{s.n}</div>
                  <div className="hstat-l">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </header>

      <hr className="divider" aria-hidden="true" />

      <main id="main">
        {/* ── Mission ── */}
        <section className="why-section" id="mission" aria-labelledby="mission-heading">
          <div className="why-inner">
            <p className="sec-label reveal">Why We Exist</p>
            <h2 className="sec-h reveal d1" id="mission-heading" style={{ maxWidth: 760 }}>
              Most of society's biggest challenges are not caused by a lack of technology.<br />
              <em>They're caused by broken systems.</em>
            </h2>
            <div className="why-cols reveal d2">
              <p className="why-body">
                Fragmented coordination. Misaligned incentives. Infrastructure bottlenecks. Unequal
                access. Institutional inertia. These are the actual barriers between transformative
                technology and the people and communities who need it most.
              </p>
              <p className="why-body">
                Mosaic Ventures exists to build the coordination layer — identifying where systems
                are breaking down and constructing ventures, partnerships, pilots, and infrastructure
                to close those gaps. The work is not framed as charity; it is built as a commercially
                durable operating model.
              </p>
            </div>
            <div className="why-pillars reveal d3">
              {[
                { icon: "⬡", label: "Venture Incubation",          desc: "Building companies at the intersection of coordination gaps and commercial opportunity" },
                { icon: "◈", label: "Infrastructure Coordination",  desc: "Connecting supply-side providers with demand-side deployers in underserved systems" },
                { icon: "◎", label: "Economic Development",         desc: "Strengthening regional ecosystems as leverage points for broader transformation" },
                { icon: "△", label: "Technology Modernization",     desc: "Reducing barriers to transformative technologies at the deployment layer" },
              ].map((p) => (
                <article className="why-pillar" key={p.label}>
                  <span className="why-icon" aria-hidden="true">{p.icon}</span>
                  <strong className="why-plabel">{p.label}</strong>
                  <p className="why-pdesc">{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <hr className="divider" aria-hidden="true" />

        {/* ── What We Do ── */}
        <section className="section" id="what-we-do" aria-labelledby="what-we-do-heading">
          <p className="sec-label reveal">What We Do</p>
          <h2 className="sec-h reveal d1" id="what-we-do-heading">
            Five principles that<br />
            <em>define how we operate</em>
          </h2>
          <div className="pillars reveal d2" role="list" aria-label="Mosaic Ventures operating principles">
            {PILLARS.map((p) => (
              <article className="pillar" key={p.num} role="listitem">
                <p className="pl-num">{p.num}</p>
                <h3 className="pl-title">{p.title}</h3>
                <p className="pl-body">{p.body}</p>
              </article>
            ))}
          </div>
        </section>

        <hr className="divider" aria-hidden="true" />

        {/* ── Initiatives ── */}
        <section className="section" id="initiatives" aria-labelledby="initiatives-heading">
          <p className="sec-label reveal">Active Initiatives</p>
          <h2 className="sec-h reveal d1" id="initiatives-heading">
            The current<br />
            <em>operational focus</em>
          </h2>
          <p className="reveal d2" style={{ margin: "16px 0 0", maxWidth: 600, fontSize: 14, lineHeight: 1.85, color: "var(--ink2)", fontWeight: 300 }}>
            AI infrastructure is the present operational wedge because it exposes a real-world
            deployment bottleneck with meaningful economic and social impact.
          </p>
          <ul className="init-list reveal d3" aria-label="Current Mosaic Ventures initiatives">
            {INITIATIVES.map((init, i) => (
              <li className={`init-item reveal d${Math.min(i + 1, 5)}`} key={init.idx}>
                <span className="init-idx" aria-hidden="true">{init.idx}</span>
                <div className="init-primary">
                  <div className="init-name-col">
                    <h3>{init.name}</h3>
                    <p className="init-full-sub">{init.full}</p>
                  </div>
                  <p className="init-desc-col">{init.desc}</p>
                </div>
                <span role="status" className="init-badge" aria-label={`Status: ${init.badge}`}>{init.badge}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Infrastructure Intelligence ── */}
        <section className="infra-section" id="conditions" aria-labelledby="conditions-heading">
          <div className="infra-inner">
            <p className="sec-label reveal">Infrastructure Intelligence</p>
            <h2 className="sec-h reveal d1" id="conditions-heading">
              Observed deployment<br />
              <em>constraints</em>
            </h2>
            <p className="reveal d2" style={{ margin: "16px 0 0", maxWidth: 600, fontSize: 14, lineHeight: 1.85, color: "var(--ink2)", fontWeight: 300 }}>
              These are the conditions Mosaic tracks operationally. Understanding infrastructure
              bottlenecks before they surface publicly is part of the core operating thesis.
            </p>
            <div className="conditions-grid reveal d3" role="list" aria-label="Atlanta infrastructure conditions and metrics">
              {INFRA_CONDITIONS.map((c) => (
                <article className="condition" key={c.cat} role="listitem">
                  <p className="cond-category">{c.cat}</p>
                  <strong className="cond-metric" aria-label={`${c.cat}: ${c.metric}`}>{c.metric}</strong>
                  <p className="cond-label">{c.label}</p>
                  <ul className="cond-signals" aria-label={`${c.cat} signals`}>
                    {c.signals.map((s) => <li className="cond-signal" key={s}>{s}</li>)}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Why Atlanta ── */}
        <section className="atl-section section" id="atlanta" aria-labelledby="atlanta-heading">
          <div className="atl-inner">
            <div>
              <p className="sec-label reveal">Why Atlanta</p>
              <h2 className="sec-h reveal d1" id="atlanta-heading">
                The initial<br />
                <em>operating wedge</em>
              </h2>
              <p className="reveal d2" style={{ margin: "16px 0 0", maxWidth: 480, fontSize: 14, lineHeight: 1.85, color: "var(--ink2)", fontWeight: 300 }}>
                Atlanta combines demand growth, infrastructure pressure, enterprise depth, and
                coordination complexity — exactly the conditions under which a real operating model
                can be proven and scaled.
              </p>
              <div className="data-grid reveal d3" aria-label="Atlanta market data">
                {ATL_DATA.map((d) => (
                  <div className="data-item" key={d.n}>
                    <div className="data-n">{d.n}</div>
                    <div className="data-lbl">{d.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div aria-hidden="true">
              <div className="atl-mosaic">
                {atlTiles.map((color, i) => (
                  <span key={i} className="am-tile" style={{ background: color }} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Thesis ── */}
        <section className="thesis-section" id="thesis" aria-labelledby="thesis-heading">
          <div className="thesis-inner">
            <p className="sec-label reveal">Thesis</p>
            <h2 className="sec-h reveal d1" id="thesis-heading">
              The principles that<br />
              <em>shape the company</em>
            </h2>
            <ol className="thesis-list reveal d2" aria-label="Mosaic Ventures thesis statements">
              {THESIS.map((item) => (
                <li className="thesis-item" key={item.num}>
                  <div className="thesis-num" aria-hidden="true">{item.num}</div>
                  <div>
                    <h3 className="thesis-title">{item.title}</h3>
                    <p className="thesis-body">{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="cta-section" id="contact" aria-labelledby="contact-heading">
          <div className="cta-bg" aria-hidden="true">MOSAIC</div>
          <div className="cta-inner">
            <h2 className="cta-h reveal d1" id="contact-heading">
              Let's build<br />
              <em>together</em>
            </h2>
            <p className="cta-sub reveal d2">
              Mosaic works with operators, institutions, communities, and mission-aligned partners
              that are serious about building durable systems.
            </p>
            <div className="cta-btns reveal d3">
              <button type="button" className="btn-gold" onClick={() => setModalOpen(true)}>Open Inquiry</button>
              <button type="button" className="btn-ghost" onClick={() => smoothScrollTo("initiatives")}>Review Initiatives</button>
            </div>
            <div className="reveal d4">
              <a className="cta-link" href="mailto:contact@mosaicventures.co">contact@mosaicventures.co</a>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="mv-footer">
        <div className="footer-brand">
          <a href="#hero" aria-label="Mosaic Ventures home">
            <img src={LOGO_SRC} alt="" width={24} height={24} />
            Mosaic Ventures
          </a>
        </div>

        <div className="footer-copy">
          © {new Date().getFullYear()} Mosaic Ventures LLC. All rights reserved.
        </div>

        <div className="footer-right">
          <div className="footer-socials" aria-label="Social media links">
            <a href="https://x.com/mosaic_ventures" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Mosaic Ventures on X">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>
            </a>
            <a href="https://www.linkedin.com/company/mosaicventures" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Mosaic Ventures on LinkedIn">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </div>
          <nav aria-label="Footer navigation">
            <div className="footer-nav">
              <Link to="/privacy">Privacy Policy</Link>        
            </div>
          </nav>
        </div>
      </footer>

      {/* ── Inquiry Modal ── */}
      {modalOpen && (
        <div
          className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-heading"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="modal-box">
            <button type="button" className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button>

            {formSent ? (
              <div className="form-success" role="status" aria-live="polite">
                <div className="form-success-icon" aria-hidden="true">✦</div>
                <h3 className="form-success-h">Message received.</h3>
                <p className="form-success-p">
                  We review every inquiry carefully. A member of the Mosaic team will follow up within 2–3 business days.
                </p>
              </div>
            ) : (
              <>
                <p className="modal-label">Work With Mosaic</p>
                <h2 className="modal-h" id="modal-heading">
                  Let's build<br /><em>together</em>
                </h2>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="pf-canary-string">System Validation Token</label>
                    <input
                      id="pf-canary-string"
                      type="text"
                      name="js_canary_validation_token"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      autoComplete="new-password" 
                      tabIndex={-1}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="pf-name">Full Name <span aria-hidden="true">*</span></label>
                      <input id="pf-name" className={`form-input${errors.name ? " input-error" : ""}`}
                        type="text" placeholder="Jane Smith" autoComplete="name"
                        value={form.name} onChange={(e) => handleChange("name", e.target.value)}
                        aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "pf-name-err" : undefined}
                      />
                      {errors.name && <p id="pf-name-err" className="form-error" role="alert">{errors.name}</p>}
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="pf-org">Organization</label>
                      <input id="pf-org" className="form-input"
                        type="text" placeholder="Company or Institution" autoComplete="organization"
                        value={form.org} onChange={(e) => handleChange("org", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="pf-email">Email <span aria-hidden="true">*</span></label>
                    <input id="pf-email" className={`form-input${errors.email ? " input-error" : ""}`}
                      type="email" placeholder="jane@company.com" autoComplete="email"
                      value={form.email} onChange={(e) => handleChange("email", e.target.value)}
                      aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "pf-email-err" : undefined}
                    />
                    {errors.email && <p id="pf-email-err" className="form-error" role="alert">{errors.email}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="pf-type">Partnership Type</label>
                    <select id="pf-type" className="form-input form-select"
                      value={form.type} onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <option value="" disabled>Select one.</option>
                      <option value="utility">Utility / Grid Infrastructure</option>
                      <option value="enterprise">Enterprise Deployment</option>
                      <option value="operator">Infrastructure Operator</option>
                      <option value="capital">Mission-Aligned Capital</option>
                      <option value="government">Government / Economic Development</option>
                      <option value="community">Community Organization</option>
                      <option value="incubation">Venture Co-Creation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="pf-message">What brings you to Mosaic?</label>
                    <textarea id="pf-message" className="form-input" rows={4}
                      placeholder="Briefly describe your interest or the opportunity you see…"
                      value={form.message} onChange={(e) => handleChange("message", e.target.value)}
                    />
                  </div>

                  {formError && <p className="form-error" role="alert">{formError}</p>}

                  <button type="submit" className="form-submit" disabled={formLoading} aria-busy={formLoading}>
                    {formLoading ? "Sending…" : "Submit Inquiry →"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Transparently wrap default export in Error Boundary containment layer
export default function MosaicVentures(): React.ReactElement {
  return (
    <MosaicErrorBoundary>
      <MosaicVenturesComponent />
    </MosaicErrorBoundary>
  );
}